import {
  // eslint-disable-next-line no-unused-vars
  h,
  Component,
  render,
  createRef,
} from 'preact';
import axios, { CancelToken } from 'axios';
import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import ResultList from './search/ResultList';

gsap.registerPlugin(ScrollToPlugin);

class SearchModule extends Component {
  state = {
    query: '',
    filter: [],
    categoriesFilter: [],
    page: 1,
    maxPages: 1,
    entries: [],
    count: [],
    loading: false,
    filterLoading: false,
    paginating: false,
    jetpack: document.body.classList.contains('jetpack-is-active'),
  };

  constructor(props) {
    super(props);

    this.cancelToken = () => {};
    this.inputRef = createRef();

    document.documentElement.addEventListener('keydown', (event) => {
      const { keyCode } = event;

      if (keyCode === 27) {
        this.closeModal();
      }
    });
  }

  handleInputChange = (event) => {
    this.setState(
      {
        page: 1,
        query: event.target.value,
        filter: [],
        maxPages: 1,
        loading: true,
        entries: [],
        count: [],
      },
      this.runSearchQuery,
    );
  };

  runSearchQuery = async () => {
    const {
      query, page, filter, jetpack, count, categoriesFilter,
    } = this.state;

    if (!query) {
      return;
    }

    try {
      this.cancelToken();

      let filterQuery = '';
      if (filter.length > 0) {
        filterQuery = `&subtype=${filter}`;
      }

      if (jetpack) {
        // Use Jetpack
        let categoriesFilterQuery = '';
        if (categoriesFilter.length > 0) {
          categoriesFilterQuery = `&cats=${categoriesFilter}`;
        }

        const searchResponse = await axios.get(
          `/wp-json/riverdale/v1/search?page=${page}&per_page=10&s=${query}${filterQuery}${categoriesFilterQuery}`,
          {
            cancelToken: new CancelToken((c) => {
              this.cancelToken = c;
            }),
          },
        );

        if (!Array.isArray(searchResponse.data.posts) || !searchResponse.data.posts.length) {
          this.setState({
            entries: false,
            maxPages: 0,
            loading: false,
            filterLoading: false,
            paginating: false,

          });
          return;
        }

        const types = searchResponse.data.post_types;
        delete types.attachment;
        delete types.wp_block;
        delete types.athletics;
        // Jetpack Post Types
        delete types.feedback;
        delete types.jp_pay_product;
        delete types.jp_pay_order;

        const categories = searchResponse.data.categories; // eslint-disable-line prefer-destructuring, max-len

        this.setState({
          entries: searchResponse.data.posts,
          maxPages: searchResponse.data.total_pages,
          loading: false,
          filterLoading: false,
          paginating: false,
          types,
          categories,
          count: filterQuery === '' ? searchResponse.data.post_types_count : count,
        });
      } else {
        // Use WP REST API
        const searchResponse = await axios.get(
          `/wp-json/wp/v2/search?page=${page}&per_page=5&search=${query}${filterQuery}&_embed`,
          {
            cancelToken: new CancelToken((c) => {
              this.cancelToken = c;
            }),
          },
        );

        const typeResponse = await axios.get('/wp-json/wp/v2/types', {
          cancelToken: new CancelToken((c) => {
            this.cancelToken = c;
          }),
        });

        const types = typeResponse;
        delete types.data.attachment;
        delete types.data.wp_block;
        delete types.athletics;

        this.setState({
          entries: searchResponse.data,
          maxPages: searchResponse.headers['x-wp-totalpages'],
          loading: false,
          filterLoading: false,
          paginating: false,
          types,
        });
      }
    } catch (e) {
      if (!axios.isCancel(e)) {
        this.setState({
          loading: false,
          filterLoading: false,
          paginating: false,
        });
      }
    }
  };

  setFilter = newFilter => (event) => {
    const { filter } = this.state;

    if (event.target.checked) {
      filter.push(newFilter);
    } else {
      const index = filter.indexOf(newFilter);
      if (index > -1) {
        filter.splice(index, 1);
      }
    }

    this.setState(
      {
        filter,
        filterLoading: true,
        page: 1,
      },
      this.runSearchQuery,
    );
  };

  setCategory = newCategoryFilter => (event) => {
    const { categoriesFilter } = this.state;

    if (event.target.checked) {
      categoriesFilter.push(newCategoryFilter);
    } else {
      const index = categoriesFilter.indexOf(newCategoryFilter);
      if (index > -1) {
        categoriesFilter.splice(index, 1);
      }
    }

    this.setState(
      {
        categoriesFilter,
        filterLoading: true,
        page: 1,
      },
      this.runSearchQuery,
    );
  };

  loadPage = (props) => {
    const { loading, paginating } = this.state;

    if (paginating || loading) {
      return;
    }

    const searchModal = document.querySelector('.search-modal');
    gsap.to(searchModal, { duration: 0.6, scrollTo: { y: 0, offsetY: 0 } });


    this.setState(
      {
        page: parseInt(props.target.innerText, 10),
        paginating: true,
      },
      this.runSearchQuery,
    );
  };

  closeModal = () => {
    document.documentElement.classList.remove('search-modal-active');
    this.inputRef.current.blur();
  };

  openModal = () => {
    document.documentElement.classList.add('search-modal-active');
    setTimeout(() => this.inputRef.current.focus(), 100);
  };

  handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) {
      this.closeModal();
    }
  };

  handleCrossKeydown = ({ keyCode }) => {
    if (keyCode === 13) {
      this.closeModal();
    }
  };

  PageNumbers = () => {
    const { maxPages, page } = this.state;
    const pages = [];
    for (let i = 1; i <= maxPages; i += 1) {
      if (i === page) {
        pages.push(<span className="page-numbers current">{i}</span>);
      } else if (
        (i < page + 3 && i > page)
        || (i > page - 3 && i < page)
        || i === 1
        || i === maxPages
      ) {
        pages.push(
          <button onClick={this.loadPage} className="page-numbers" tabIndex="0" type="button">
            {i}
          </button>,
        );
      } else if (i === page + 3 || i === page - 3) {
        pages.push(
          <button className="page-numbers" tabIndex="0" type="button" disabled>
            ...
          </button>,
        );
      }
    }

    return <div className="pagination">{pages}</div>;
  };

  render() {
    const {
      filter,
      query,
      entries,
      page,
      paginating,
      maxPages,
      loading,
      filterLoading,
      types,
      jetpack,
      count,
      categories,
      categoriesFilter,
    } = this.state;
    const { PageNumbers } = this;

    return (
      <div className="search-modal-wrapper active">
        <div className="search-modal">
          <div className="search-modal-header">
            <div className="search-modal-header-inner">
              <span className="header-logo" />
              <div className="modal-header--form riverdale-form">
                <div className="input-css-search">
                  <input
                    id="input-focus"
                    ref={this.inputRef}
                    type="text"
                    onInput={this.handleInputChange}
                    value={query}
                    placeholder="What are you looking for?"
                    onKeyDown={this.handleKeyDown}
                  />
                  <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 20.2 20.8" xmlSpace="preserve">
                    <title>Icon Search</title>
                    <path fill="#872242" d="M20.2,19.4L13.8,13c0.5-0.6,1-1.3,1.3-2.1c0.4-1,0.6-2,0.6-3.1c0-4.3-3.5-7.9-7.9-7.9S0,3.5,0,7.9c0,4.3,3.5,7.9,7.9,7.9c1.6,0,3.2-0.5,4.5-1.4l6.4,6.4L20.2,19.4z M2,7.9C2,4.6,4.6,2,7.9,2s5.9,2.6,5.9,5.9c0,0.8-0.2,1.6-0.5,2.3c-0.9,2.2-3,3.6-5.4,3.6C4.6,13.8,2,11.1,2,7.9z" />
                  </svg>
                </div>
              </div>
              <div className="primaryNav-search">
                <button
                  type="button"
                  className="primaryNav-search-button"
                  onClick={this.closeModal}
                  onKeyDown={this.handleCrossKeydown}
                >
                  <div
                    className="cross"
                  >
                    <div className="cross-box">
                      <div className="cross-inner" />
                    </div>
                  </div>
                  <span>
                    Close
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div id="search-modal-top" className="search-modal-results">
            <section className="container">
              <div className="section-wrapper">
                {!loading && entries.length !== 0 && query.length !== 0 && jetpack && (
                  <div className="wp-block-riverdale-columns">
                    <div className="wp-block-riverdale-column">
                      <h4>Filter By Type</h4>
                      <div className="search-modal-results-filters riverdale-form">
                        {types && Object.keys(types).map(key => (
                          <label className="label-checkbox" htmlFor={`post-type-${key}`}>
                            <input
                              checked={filter.includes(key)}
                              onClick={this.setFilter(key)}
                              type="checkbox"
                              id={`post-type-${key}`}
                              name="post-type"
                              value={key}
                              disabled={!count[key]}
                            />
                            <span className={!count[key] && 'label-checkbox--disabled'}>
                              <p>
                                {types[key].label === 'Posts' ? 'Stories' : types[key].label}
                                {count[key] ? ` (${count[key]})` : ' (0)'}
                              </p>
                            </span>
                          </label>
                        ))}
                      </div>
                      <h4>Filter By Category</h4>
                      <div className="search-modal-results-filters riverdale-form">
                        {categories && Object.keys(categories).map(key => (
                          <label
                            className="label-checkbox"
                            htmlFor={`category-${categories[key].slug}`}
                          >
                            <input
                              checked={categoriesFilter.includes(categories[key].slug)}
                              onClick={this.setCategory(categories[key].slug)}
                              type="checkbox"
                              id={`category-${categories[key].slug}`}
                              name="category"
                              value={categories[key].slug}
                              disabled={filter.length > 0 && !filter.includes('page') && !filter.includes('post')}
                            />
                            <span className={filter.length > 0 && !filter.includes('page') && !filter.includes('post') && 'label-checkbox--disabled'}>
                              <p>{categories[key].name}</p>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="wp-block-riverdale-column">
                      {!loading && entries.length !== 0 && (
                        <h3>{`Search results for '${query}'`}</h3>
                      )}
                      {!filterLoading && !paginating && entries !== false && (
                        <div className="post-list">
                          <ResultList entries={entries} jetpack={jetpack} types={types} />
                        </div>
                      )}
                      {maxPages >= page
                        && maxPages > 1
                        && !filterLoading
                        && !paginating
                        && entries !== false && <div className="grid-footer">{PageNumbers()}</div>}
                      {(filterLoading || paginating) && (
                        <p className="riverdale-loader riverdale-loader--small">
                          <span className="riverdale-spinner">Loading...</span>
                          Please wait
                        </p>
                      )}
                      {!filterLoading && !paginating && entries === false && (
                        <p>No results found</p>
                      )}
                    </div>
                  </div>
                )}
                {!loading && entries.length !== 0 && query.length !== 0 && !jetpack && (
                  <div>
                    {!loading && entries.length !== 0 && <h3>{`Search results for '${query}'`}</h3>}
                    {!filterLoading && !paginating && entries !== false && (
                      <div className="post-list">
                        <ResultList entries={entries} jetpack={jetpack} types={types} />
                      </div>
                    )}
                    {maxPages >= page
                      && maxPages > 1
                      && !filterLoading
                      && !paginating
                      && entries !== false
                      && <div className="grid-footer">{PageNumbers()}</div>}
                    {(filterLoading || paginating) && (
                      <p className="riverdale-loader riverdale-loader--small">
                        <span className="riverdale-spinner">Loading...</span>
                        Please wait
                      </p>
                    )}
                    {!filterLoading && !paginating && entries === false && <p>No results found</p>}
                  </div>
                )}
                {loading && !paginating && entries.length === 0 && query.length !== 0 && (
                  <div className="riverdale-loader-wrap">
                    <p className="riverdale-loader">
                      <span className="riverdale-spinner">Loading...</span>
                      Please wait
                    </p>
                  </div>
                )}
                {!loading && !paginating && entries.length === 0 && query.length !== 0 && (
                  <h2>No results found</h2>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

const setup = () => {
  const $ele = document.getElementById('search-module');
  if ($ele) {
    render(<SearchModule />, $ele);
  }
};
setup();
